import { contextoJornada, registrarToqueJornada } from "./atribuicao-jornada.js";
import { EVENTO_ENTRADA_REMARKETING, registrarEntradaRemarketing } from "./remarketing.js";
import type { AtribuicaoListaEspera } from "@ida/core";

/**
 * Telemetria de produto com replay seguro. O painel tem CPF, valores, nomes,
 * conversas e documentos; por isso inputs e todo texto das paginas privadas
 * sao mascarados, atributos sao removidos e corpos/headers de rede nunca sao
 * gravados. Eventos de negocio sao explicitos e nao carregam PII.
 */
const CHAVE = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const META_PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID as string | undefined;
const GOOGLE_ANALYTICS_ID = import.meta.env.VITE_GOOGLE_ANALYTICS_ID as string | undefined;
const GOOGLE_ADS_ID = import.meta.env.VITE_GOOGLE_ADS_ID as string | undefined;
const GOOGLE_ADS_ONBOARDING_LABEL = import.meta.env
  .VITE_GOOGLE_ADS_ONBOARDING_LABEL as string | undefined;
const GOOGLE_ADS_PURCHASE_LABEL = import.meta.env
  .VITE_GOOGLE_ADS_PURCHASE_LABEL as string | undefined;
const CHAVE_ATRIBUICAO = "ida.atribuicao.primeiro";
const CHAVE_PREFERENCIA_ANUNCIOS = "ida.preferencia.anuncios";
const CHAVE_PREFERENCIA_META = "ida.preferencia.meta";
const CHAVE_CONSENTIMENTO_META_ANTIGA = "ida.consentimento.meta";
let carregando: ReturnType<typeof carregar> | undefined;

type PreferenciaAnuncios = "ativa" | "desativada";
type ChamadaFbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[][];
  loaded: boolean;
  version: string;
};

declare global {
  interface Window {
    fbq?: ChamadaFbq;
    _fbq?: ChamadaFbq;
    dataLayer?: Array<ArrayLike<unknown>>;
    gtag?: (...args: unknown[]) => void;
  }
}

interface EventoMeta {
  metodo: "track" | "trackCustom";
  nome: string;
  dados?: Record<string, string>;
  id: string;
}

interface EventoGoogle {
  nome:
    | "generate_lead"
    | "waitlist_joined"
    | "onboarding_started"
    | "sign_up"
    | "trial_started"
    | "begin_checkout"
    | "purchase";
  rotulo?: string;
}

/**
 * Allowlist deliberada. Eventos fiscais, suporte e administracao ficam no
 * PostHog e nunca chegam a uma plataforma de anuncios.
 */
const EVENTOS_META: Record<string, Omit<EventoMeta, "id">> = {
  landing_visualizada: {
    metodo: "track", nome: "ViewContent", dados: { content_name: "landing" },
  },
  simulador_preco_usado: { metodo: "trackCustom", nome: "PricingCalculatorUsed" },
  cta_landing_clicado: { metodo: "trackCustom", nome: "LandingCTAClick" },
  diagnostico_resultado: { metodo: "trackCustom", nome: "DiagnosisCompleted" },
  diagnostico_lead: { metodo: "track", nome: "Lead" },
  abriu_login: { metodo: "trackCustom", nome: "LoginOpened" },
  login_concluido: { metodo: "trackCustom", nome: "LoginCompleted" },
  onboarding_interface_iniciado: { metodo: "trackCustom", nome: "OnboardingStarted" },
  assinatura_iniciada: { metodo: "track", nome: "InitiateCheckout" },
};

/** Eventos recomendados do GA4 e conversoes explicitamente criadas no Ads. */
const EVENTOS_GOOGLE: Record<string, EventoGoogle> = {
  diagnostico_lead: { nome: "generate_lead" },
  // A waitlist serve para medir demanda, nunca para orientar lances.
  lista_espera_interface_concluida: { nome: "waitlist_joined" },
  onboarding_interface_iniciado: { nome: "onboarding_started" },
  // A conversão usada nos lances só ocorre após o servidor concluir o cadastro.
  onboarding_interface_concluida: {
    nome: "sign_up", rotulo: GOOGLE_ADS_ONBOARDING_LABEL,
  },
  trial_interface_iniciado: { nome: "trial_started" },
  assinatura_iniciada: { nome: "begin_checkout" },
  assinatura_paga_interface: {
    nome: "purchase", rotulo: GOOGLE_ADS_PURCHASE_LABEL,
  },
};

let metaIniciado = false;
let googleIniciado = false;
let navegacaoAnunciosInstalada = false;
let preferenciaAnunciosMemoria: PreferenciaAnuncios | undefined;

const chavesPessoais = /(email|e-mail|nome|cpf|cnpj|telefone|phone|endereco|inquilino|locador|mensagem|texto|documento|arquivo)/i;
const pareceEmail = /[\w.+-]+@[\w.-]+\.[a-z]{2,}/i;
const pareceDocumento = /\b\d{3}[.-]?\d{3}[.-]?\d{3}[-/]?\d{2}\b/;

function limparProps(props: Record<string, unknown> | undefined): Record<string, unknown> {
  const limpas: Record<string, unknown> = {};
  for (const [chave, valor] of Object.entries(props ?? {})) {
    // Campos internos de deduplicacao nao pertencem ao produto nem ao replay.
    if (chave.startsWith("_")) continue;
    if (chavesPessoais.test(chave)) continue;
    if (["$current_url", "$referrer", "$initial_current_url", "$initial_referrer"]
      .includes(chave) && typeof valor === "string") {
      try {
        const url = new URL(valor, window.location.origin);
        limpas[chave] = chave.includes("referrer") ? url.hostname : `${url.origin}${url.pathname}`;
      } catch { limpas[chave] = "[url inválida]"; }
      continue;
    }
    if (typeof valor === "string" && (pareceEmail.test(valor) || pareceDocumento.test(valor))) {
      limpas[chave] = "[oculto]";
      continue;
    }
    limpas[chave] = valor;
  }
  return limpas;
}

async function carregar() {
  if (!CHAVE) return null;
  const { default: posthog } = await import("posthog-js");
  posthog.init(CHAVE, {
    api_host:
      (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ??
      "https://us.i.posthog.com",
    capture_pageview: "history_change",
    autocapture: false,
    disable_session_recording: false,
    // O projeto habilita console remotamente. Este cliente fiscal o desliga:
    // erros podem carregar contexto pessoal mesmo com o DOM mascarado.
    enable_recording_console_log: false,
    person_profiles: "identified_only",
    ip: false,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "main.pagina, main.painel, .modal-painel, .conversa, [data-ph-mask]",
      blockSelector: ".ph-no-capture, [data-ph-no-capture], iframe, object, embed, canvas, img[src^='data:']",
      maskAttributeFn: (nome, valor) => {
        if (["value", "placeholder", "title", "aria-label", "alt"].includes(nome)
            || nome.startsWith("data-")) return "[oculto]";
        if (nome === "href") {
          try {
            const url = new URL(valor, window.location.origin);
            return url.protocol.startsWith("http") ? `${url.origin}${url.pathname}` : "#";
          } catch { return "#"; }
        }
        if (nome === "src" && valor.startsWith("data:")) return "";
        return valor;
      },
      recordHeaders: false,
      recordBody: false,
      captureCanvas: { recordCanvas: false },
      captureJsonLd: false,
    },
    before_send: (captura) => {
      if (!captura) return captura;
      return { ...captura, properties: limparProps(captura.properties) };
    },
  });
  return posthog;
}

function cliente() {
  if (!carregando) carregando = carregar();
  return carregando;
}

function idEvento(): string {
  return globalThis.crypto?.randomUUID?.()
    ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function preferenciaAnuncios(): PreferenciaAnuncios {
  if (preferenciaAnunciosMemoria) return preferenciaAnunciosMemoria;
  try {
    const valor = localStorage.getItem(CHAVE_PREFERENCIA_ANUNCIOS)
      ?? localStorage.getItem(CHAVE_PREFERENCIA_META);
    if (valor === "ativa" || valor === "desativada") return valor;
    // Preserva uma recusa feita durante a versao anterior ao opt-out.
    return localStorage.getItem(CHAVE_CONSENTIMENTO_META_ANTIGA) === "recusado"
      ? "desativada" : "ativa";
  } catch { return "ativa"; }
}

function cookie(nome: string): string | undefined {
  const prefixo = `${nome}=`;
  const parte = document.cookie.split(";").map((p) => p.trim())
    .find((p) => p.startsWith(prefixo));
  if (!parte) return undefined;
  try { return decodeURIComponent(parte.slice(prefixo.length)); }
  catch { return parte.slice(prefixo.length); }
}

function atualizarAtribuicaoAnuncios(preferencia: PreferenciaAnuncios): void {
  try {
    const base = capturarAtribuicao() ?? {};
    const atualizada: AtribuicaoListaEspera = {
      ...base,
      metaMedicao: preferencia,
      googleMedicao: preferencia,
    };
    atualizada.metaPreferenciaEm ??= new Date().toISOString();
    atualizada.googlePreferenciaEm ??= new Date().toISOString();
    if (preferencia === "ativa") {
      const fbp = cookie("_fbp")?.slice(0, 240);
      const fbcCookie = cookie("_fbc")?.slice(0, 600);
      const query = new URLSearchParams(window.location.search);
      const fbclid = query.get("fbclid")?.slice(0, 500);
      if (fbp) atualizada.metaFbp = fbp;
      if (fbcCookie) atualizada.metaFbc = fbcCookie;
      else if (fbclid) atualizada.metaFbc = `fb.1.${Date.now()}.${fbclid}`;
      const gclid = query.get("gclid")?.slice(0, 500);
      const gbraid = query.get("gbraid")?.slice(0, 500);
      const wbraid = query.get("wbraid")?.slice(0, 500);
      if (gclid) atualizada.googleGclid = gclid;
      if (gbraid) atualizada.googleGbraid = gbraid;
      if (wbraid) atualizada.googleWbraid = wbraid;
    } else {
      delete atualizada.metaFbp;
      delete atualizada.metaFbc;
      delete atualizada.googleGclid;
      delete atualizada.googleGbraid;
      delete atualizada.googleWbraid;
    }
    localStorage.setItem(CHAVE_ATRIBUICAO, JSON.stringify(atualizada));
  } catch { /* sem storage/cookies, a preferencia ainda vale nesta pagina */ }
}

function paginaPublicaParaAnuncios(): boolean {
  const caminho = window.location.pathname;
  return caminho === "/" || caminho === "/painel"
    || caminho === "/calculadora-imposto-aluguel"
    || caminho === "/termos" || caminho === "/privacidade";
}

function enviarMeta(eventoMeta: EventoMeta): void {
  if (!window.fbq || preferenciaAnuncios() !== "ativa") return;
  window.fbq(eventoMeta.metodo, eventoMeta.nome, eventoMeta.dados ?? {}, {
    eventID: eventoMeta.id,
  });
}

function entradaRemarketingPostHog(): void {
  if (!CHAVE || preferenciaAnuncios() !== "ativa" || !paginaPublicaParaAnuncios()) return;
  try {
    registrarEntradaRemarketing({ agora: Date.now(), primeiroAcesso: capturarAtribuicao()?.primeiroAcessoEm,
      plataforma: "posthog", storage: localStorage,
      enviar: () => { void cliente().then(p => p?.capture("remarketing_elegivel", limparProps({
        ...propriedadesJornada(), coorte: "20260909", janela_dias: 14,
        entrada_em: capturarAtribuicao()?.primeiroAcessoEm,
      }))).catch(() => undefined); },
    });
  } catch { /* Não interfere no produto. */ }
}

function entradaRemarketing(plataforma: "meta" | "google"): void {
  if (preferenciaAnuncios() !== "ativa" || !paginaPublicaParaAnuncios()) return;
  if (plataforma === "meta" ? !window.fbq : !window.gtag || !googleAnalyticsId()) return;
  try {
    registrarEntradaRemarketing({
      agora: Date.now(), primeiroAcesso: capturarAtribuicao()?.primeiroAcessoEm,
      plataforma, storage: localStorage,
      enviar: () => {
        if (plataforma === "meta") {
          enviarMeta({ metodo: "trackCustom", nome: EVENTO_ENTRADA_REMARKETING, id: idEvento() });
        } else {
          window.gtag?.("event", EVENTO_ENTRADA_REMARKETING, {
            send_to: googleAnalyticsId(),
            page_location: `${window.location.origin}${window.location.pathname}`,
          });
        }
      },
    });
  } catch { /* Não interfere no carregamento ou na navegação. */ }
}

function paginaMeta(): void {
  if (!paginaPublicaParaAnuncios()) return;
  enviarMeta({ metodo: "track", nome: "PageView", id: idEvento() });
  entradaRemarketing("meta");
  entradaRemarketingPostHog();
}

function instalarNavegacaoAnuncios(): void {
  if (navegacaoAnunciosInstalada) return;
  navegacaoAnunciosInstalada = true;
  let ultimoCaminho = window.location.pathname;
  const mudou = () => {
    queueMicrotask(() => {
      if (window.location.pathname === ultimoCaminho) return;
      ultimoCaminho = window.location.pathname;
      capturarToqueJornada();
      paginaMeta();
      paginaGoogle();
    });
  };
  window.addEventListener("popstate", mudou);
  const pushState = window.history.pushState.bind(window.history);
  const replaceState = window.history.replaceState.bind(window.history);
  window.history.pushState = (data: unknown, unused: string, url?: string | URL | null) => {
    pushState(data, unused, url);
    mudou();
  };
  window.history.replaceState = (data: unknown, unused: string, url?: string | URL | null) => {
    replaceState(data, unused, url);
    mudou();
  };
}

function iniciarMeta(): void {
  if (!META_PIXEL_ID || preferenciaAnuncios() !== "ativa") return;
  if (metaIniciado) return;
  metaIniciado = true;

  let fbq = window.fbq;
  if (!fbq) {
    fbq = ((...args: unknown[]) => {
      if (fbq?.callMethod) fbq.callMethod(...args);
      else fbq?.queue.push(args);
    }) as ChamadaFbq;
    fbq.queue = [];
    fbq.loaded = true;
    fbq.version = "2.0";
    window.fbq = fbq;
    window._fbq = fbq;
  }

  fbq("consent", "grant");
  fbq("set", "autoConfig", false, META_PIXEL_ID);
  fbq("init", META_PIXEL_ID);
  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  script.dataset.finalidade = "medicao-anuncios-meta";
  document.head.append(script);

  paginaMeta();
  window.setTimeout(() => atualizarAtribuicaoAnuncios("ativa"), 1_000);
}

function googleAnalyticsId(): string | undefined {
  const id = GOOGLE_ANALYTICS_ID?.trim();
  return id && /^G-[A-Z0-9]+$/i.test(id) ? id : undefined;
}

function googleAdsId(): string | undefined {
  const id = GOOGLE_ADS_ID?.trim();
  return id && /^AW-\d+$/i.test(id) ? id : undefined;
}

function googleConfigurado(): boolean {
  return Boolean(googleAnalyticsId() || googleAdsId());
}

function paginaGoogle(): void {
  const analyticsId = googleAnalyticsId();
  if (!analyticsId || !window.gtag || preferenciaAnuncios() !== "ativa"
      || !paginaPublicaParaAnuncios()) return;
  window.gtag("event", "page_view", {
    send_to: analyticsId,
    page_location: `${window.location.origin}${window.location.pathname}`,
    page_path: window.location.pathname,
  });
  entradaRemarketing("google");
  entradaRemarketingPostHog();
}

function iniciarGoogle(): void {
  if (!googleConfigurado() || preferenciaAnuncios() !== "ativa" || googleIniciado) return;
  const analyticsId = googleAnalyticsId();
  const adsId = googleAdsId();
  const principal = analyticsId ?? adsId;
  if (!principal) return;
  googleIniciado = true;

  window.dataLayer ??= [];
  // O carregador do Google consome objetos `arguments`, exatamente como no
  // snippet oficial. Enfileirar um Array parece equivalente, mas a biblioteca
  // não o reconhece como um comando gtag em todos os carregamentos.
  window.gtag ??= function gtag(..._args: unknown[]) {
    window.dataLayer!.push(arguments);
  };
  window.gtag("consent", "default", {
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
    analytics_storage: "granted",
  });
  // Impede a deteccao automatica de email, telefone, nome ou endereco pelo
  // Google tag, mesmo que essa capacidade esteja ligada na conta.
  window.gtag("set", "user_data", null);
  window.gtag("set", "ads_data_redaction", false);
  window.gtag("js", new Date());
  if (analyticsId) {
    window.gtag("config", analyticsId, {
      send_page_view: false,
      allow_google_signals: false,
      allow_ad_personalization_signals: true,
    });
  }
  if (adsId && adsId !== analyticsId) {
    window.gtag("config", adsId, {
      send_page_view: false,
      allow_ad_personalization_signals: true,
    });
  }
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(principal)}`;
  script.dataset.finalidade = "medicao-anuncios-google";
  document.head.append(script);
  paginaGoogle();
}

async function hashTecnico(valor: string | undefined): Promise<string | undefined> {
  if (!valor || !globalThis.crypto?.subtle) return undefined;
  const bytes = new TextEncoder().encode(valor);
  const resumo = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(resumo), (b) => b.toString(16).padStart(2, "0")).join("");
}

async function enviarGoogle(nome: string, props?: Record<string, unknown>): Promise<void> {
  const configuracao = EVENTOS_GOOGLE[nome];
  if (!configuracao || !googleConfigurado() || preferenciaAnuncios() !== "ativa") return;
  iniciarGoogle();
  if (!window.gtag) return;

  const valor = Number(props?.valor);
  const parametros: Record<string, string | number> = {
    page_location: `${window.location.origin}${window.location.pathname}`,
    ...(Number.isFinite(valor) && valor >= 0 ? { value: valor, currency: "BRL" } : {}),
  };
  const transacao = await hashTecnico(
    typeof props?._googleTransacao === "string" ? props._googleTransacao : undefined,
  );
  if (transacao) parametros.transaction_id = transacao;

  const analyticsId = googleAnalyticsId();
  if (analyticsId) {
    window.gtag("event", configuracao.nome, { ...parametros, send_to: analyticsId });
  }
  const adsId = googleAdsId();
  const rotulo = configuracao.rotulo?.trim();
  if (adsId && rotulo && /^[A-Za-z0-9_-]{3,100}$/.test(rotulo)) {
    window.gtag("event", "conversion", {
      ...parametros,
      send_to: `${adsId}/${rotulo}`,
    });
  }
}

function apagarCookie(nome: string): void {
  document.cookie = `${nome}=; Max-Age=0; path=/; SameSite=Lax`;
  if (window.location.hostname.endsWith("alugando.com")) {
    document.cookie = `${nome}=; Max-Age=0; path=/; domain=.alugando.com; SameSite=Lax`;
  }
}

function apagarCookiesGoogle(): void {
  for (const parte of document.cookie.split(";")) {
    const nome = parte.split("=", 1)[0]?.trim();
    if (nome && (/^_ga(?:_|$)/.test(nome) || /^_gcl_/.test(nome)
      || nome === "_gid" || nome === "_gat")) apagarCookie(nome);
  }
}

function definirPreferenciaAnuncios(preferencia: PreferenciaAnuncios): void {
  preferenciaAnunciosMemoria = preferencia;
  try {
    localStorage.setItem(CHAVE_PREFERENCIA_ANUNCIOS, preferencia);
    localStorage.setItem(CHAVE_PREFERENCIA_META, preferencia);
    localStorage.removeItem(CHAVE_CONSENTIMENTO_META_ANTIGA);
  } catch { /* a decisao continua valendo ate a pagina fechar */ }
  atualizarAtribuicaoAnuncios(preferencia);
  document.querySelector(".preferencia-meta")?.remove();
  if (preferencia === "ativa") {
    if (metaIniciado) {
      window.fbq?.("consent", "grant");
      paginaMeta();
    } else iniciarMeta();
    if (googleIniciado) {
      window.gtag?.("consent", "update", {
        ad_storage: "granted",
        ad_user_data: "granted",
        ad_personalization: "granted",
        analytics_storage: "granted",
      });
      window.gtag?.("set", "ads_data_redaction", false);
      paginaGoogle();
    } else iniciarGoogle();
  }
  else {
    window.fbq?.("consent", "revoke");
    window.gtag?.("consent", "update", {
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      analytics_storage: "denied",
    });
    window.gtag?.("set", "ads_data_redaction", true);
    apagarCookie("_fbp");
    apagarCookie("_fbc");
    apagarCookiesGoogle();
  }
  window.dispatchEvent(new CustomEvent("ida:preferencia-anuncios", {
    detail: { preferencia },
  }));
}

function mostrarPreferenciaAnuncios(): void {
  if ((!META_PIXEL_ID && !googleConfigurado())
      || document.querySelector(".preferencia-meta")) return;
  if (!document.body) {
    document.addEventListener("DOMContentLoaded", () => mostrarPreferenciaAnuncios(), {
      once: true,
    });
    return;
  }

  const ativa = preferenciaAnuncios() === "ativa";
  const painel = document.createElement("aside");
  painel.className = "preferencia-meta";
  painel.setAttribute("role", "dialog");
  painel.setAttribute("aria-label", "Configurar medição de anúncios");

  const texto = document.createElement("div");
  texto.className = "preferencia-meta__texto";
  const titulo = document.createElement("strong");
  titulo.textContent = "Medição de anúncios";
  const explicacao = document.createElement("span");
  const plataformas = [META_PIXEL_ID ? "Meta" : "", googleConfigurado() ? "Google" : ""]
    .filter(Boolean).join(" e ");
  const verbo = plataformas.includes(" e ") ? "medem" : "mede";
  explicacao.textContent = ativa
    ? `Está ativa. ${plataformas} ${verbo} visitas e cadastros e podem mostrar anúncios do Alugando novamente, sem receber seus dados fiscais.`
    : `Está desativada. Nenhum novo evento de anúncio será enviado à ${plataformas}.`;
  const politica = document.createElement("a");
  politica.href = "/privacidade";
  politica.textContent = "Ver privacidade";
  texto.append(titulo, explicacao, politica);

  const acoes = document.createElement("div");
  acoes.className = "preferencia-meta__acoes";
  const fechar = document.createElement("button");
  fechar.type = "button";
  fechar.className = "preferencia-meta__fechar";
  fechar.textContent = ativa ? "Manter ativa" : "Manter desativada";
  fechar.addEventListener("click", () => painel.remove());
  const alternar = document.createElement("button");
  alternar.type = "button";
  alternar.className = "preferencia-meta__alternar";
  alternar.textContent = ativa ? "Desativar medição" : "Ativar medição";
  alternar.addEventListener("click", () => definirPreferenciaAnuncios(
    ativa ? "desativada" : "ativa"));
  acoes.append(fechar, alternar);
  painel.append(texto, acoes);
  document.body.append(painel);
}

/** Abre a preferencia de opt-out sem interromper a primeira visita. */
export function abrirPreferenciasMedicao(): void {
  mostrarPreferenciaAnuncios();
}

function capturarToqueJornada(): void {
  if (!CHAVE || preferenciaAnuncios() !== "ativa") return;
  try {
    const toque = registrarToqueJornada({
      url: window.location.href, referencia: document.referrer, agora: Date.now(),
      primeira: capturarAtribuicao(), storage: localStorage, sessao: sessionStorage,
    });
    if (toque) void cliente().then(p => p?.capture("aquisicao_toque", limparProps(toque))).catch(() => undefined);
  } catch { /* Aquisição nunca bloqueia a experiência. */ }
}

function propriedadesJornada(): Record<string, string> {
  if (preferenciaAnuncios() !== "ativa") return {};
  try { return contextoJornada(localStorage); } catch { return {}; }
}

export function iniciarTelemetria(): void {
  capturarAtribuicao();
  capturarToqueJornada();
  void cliente();
  instalarNavegacaoAnuncios();
  atualizarAtribuicaoAnuncios(preferenciaAnuncios());
  if (preferenciaAnuncios() === "ativa") {
    iniciarMeta();
    iniciarGoogle();
    entradaRemarketingPostHog();
  }
}

export function propriedadesComAtribuicao(
  props: Record<string, unknown> | undefined,
  atribuicao: AtribuicaoListaEspera | undefined,
): Record<string, unknown> {
  return {
    ...(props ?? {}),
    ...(atribuicao?.origem ? { origem: atribuicao.origem } : {}),
    ...(atribuicao?.midia ? { midia: atribuicao.midia } : {}),
    ...(atribuicao?.campanha ? { campanha: atribuicao.campanha } : {}),
    ...(atribuicao?.conteudo ? { conteudo: atribuicao.conteudo } : {}),
  };
}

export function evento(nome: string, props?: Record<string, unknown>): void {
  if (CHAVE) {
    const propriedades = { ...propriedadesComAtribuicao(props, capturarAtribuicao()), ...propriedadesJornada() };
    void cliente().then((posthog) => posthog?.capture(nome, limparProps(propriedades)));
  }
  const mapeado = EVENTOS_META[nome];
  if (mapeado && META_PIXEL_ID && preferenciaAnuncios() === "ativa") {
    const idCompartilhado = typeof props?._metaEventoId === "string"
      && /^[A-Za-z0-9:_-]{8,160}$/.test(props._metaEventoId)
      ? props._metaEventoId : undefined;
    const paraMeta = { ...mapeado, id: idCompartilhado ?? idEvento() };
    iniciarMeta();
    enviarMeta(paraMeta);
  }
  void enviarGoogle(nome, props).catch(() => undefined);
}

/** Conversao exclusiva do Google quando o PostHog ja recebeu o fato canonico do servidor. */
export function conversaoGoogle(nome: string, props?: Record<string, unknown>): void {
  void enviarGoogle(nome, props).catch(() => undefined);
}

export function identificar(uid: string): void {
  if (!CHAVE) return;
  void cliente().then((posthog) => {
    posthog?.identify(uid);
    posthog?.capture("aquisicao_identificada", limparProps(propriedadesJornada()));
  }).catch(() => undefined);
}

/** Identificador tecnico para ligar um relato ao replay, sem expor o conteudo. */
export async function sessaoTelemetria(): Promise<string | undefined> {
  if (!CHAVE) return undefined;
  const posthog = await cliente();
  const id = posthog?.get_session_id?.();
  return typeof id === "string" && id ? id.slice(0, 160) : undefined;
}

function hostSeguro(url: string): string | undefined {
  try { return new URL(url).hostname.slice(0, 180); } catch { return undefined; }
}

export function capturarAtribuicao(): AtribuicaoListaEspera | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const lembrada = localStorage.getItem(CHAVE_ATRIBUICAO);
    if (lembrada) return JSON.parse(lembrada) as AtribuicaoListaEspera;

    const query = new URLSearchParams(window.location.search);
    const referencia = hostSeguro(document.referrer);
    const origemUtm = query.get("utm_source")?.slice(0, 80) || undefined;
    const buscaOrganica = Boolean(
      referencia && /(^|\.)(google|bing|yahoo|duckduckgo)\./i.test(referencia));
    const atribuicao: AtribuicaoListaEspera = {
      origem: origemUtm ?? (buscaOrganica
        ? referencia!.split(".").at(-2)
        : referencia ?? "direto"),
      ...(query.get("utm_medium") || buscaOrganica
        ? { midia: query.get("utm_medium")?.slice(0, 80) || "organic" } : {}),
      ...(query.get("utm_campaign")
        ? { campanha: query.get("utm_campaign")!.slice(0, 120) } : {}),
      ...(query.get("utm_term") ? { termo: query.get("utm_term")!.slice(0, 120) } : {}),
      ...(query.get("utm_content")
        ? { conteudo: query.get("utm_content")!.slice(0, 120) } : {}),
      ...(referencia ? { referencia } : {}),
      paginaEntrada: window.location.pathname.slice(0, 120),
      primeiroAcessoEm: new Date().toISOString(),
    };
    localStorage.setItem(CHAVE_ATRIBUICAO, JSON.stringify(atribuicao));
    return atribuicao;
  } catch { return undefined; }
}

export function resetarTelemetria(): void {
  if (!CHAVE) return;
  void cliente().then((posthog) => posthog?.reset());
}
